/**
 * 板発注画面 板情報パネルの見出し / フッター指標算出 (AC-BIN-026)。
 *
 * どちらも取得済みデータ (約定ティッカー / pivot 済み板) から算出する純関数
 * (= vitest 対象)。 proto / backend の追加取得は行わない。
 */
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { BiddingMarketContract } from '@ebalance/proto/bffuser/v1/bidding_intraday_market_model_pb'

import { decimalToNumber } from '~/models/decimal'

import type { MarketBoardRow } from './marketBoardPivot'

/** 板情報パネル見出しの 3 指標 (AC-BIN-026: 最終約定価格 / 約定量 / 約定件数)。 */
export type MarketBoardTradeSummary = {
  /** 最終約定価格 (円/kWh)。 選択中コマに約定実績が無い場合は undefined。 */
  lastPrice: string | undefined
  /** 約定量合計 (MW、 コマ内の全約定の単純合算)。 実績が無い場合は '0'。 */
  executedVolume: string
  /** 約定件数。 */
  executedCount: number
}

function contractedAtMillis(contract: BiddingMarketContract): number {
  return contract.contractedAt
    ? timestampDate(contract.contractedAt).getTime()
    : Number.NEGATIVE_INFINITY
}

/**
 * 約定履歴を `contracted_at` の新しい順に並べ替える (約定ティッカーと
 * 板情報パネル見出しの最終約定価格が同じ「最新」を指すための単一の判定)。
 *
 * backend は上流の順序をそのまま返し並び順を保証しないため、 配列順に依存しない。
 * `contracted_at` が同時刻・欠落の約定どうしは配列の後ろにあるものを新しいとみなす。
 */
export function sortContractsLatestFirst(
  contracts: BiddingMarketContract[],
): BiddingMarketContract[] {
  return [...contracts]
    .reverse()
    .sort((a, b) => contractedAtMillis(b) - contractedAtMillis(a) || 0)
}

/**
 * 約定ティッカーの前約定比変化 (+/−、 AC-BIN-031)。 `sortContractsLatestFirst` で並べた
 * 約定ごとに、 1 つ古い約定の価格との差 (円/kWh、 小数 2 桁に丸める) を返す。
 * 最も古い約定と、 価格が欠けている約定は比べる相手が無いので undefined。
 *
 * ティッカーは 100 件ずつ描画する (#9581) が、 差は全件の並びで取る
 * (= 100 件目の差も 101 件目の約定と比べる。 窓の境目で差が消えない)。
 */
export function computeContractPriceChanges(
  contractsLatestFirst: BiddingMarketContract[],
): Array<number | undefined> {
  return contractsLatestFirst.map((contract, index) => {
    const price = decimalToNumber(contract.price)
    const previousPrice = decimalToNumber(
      contractsLatestFirst[index + 1]?.price,
    )
    if (price === undefined || previousPrice === undefined) return undefined
    return Math.round((price - previousPrice) * 100) / 100
  })
}

/**
 * 選択中コマの約定履歴 (約定ティッカーと同一データ、 `ListBiddingMarketContracts`)
 * から板情報パネル見出しの 3 指標を算出する。
 *
 * 最終約定価格は `sortContractsLatestFirst` の先頭 (= 約定ティッカーの先頭行) の価格。
 */
export function computeMarketBoardTradeSummary(
  contracts: BiddingMarketContract[],
): MarketBoardTradeSummary {
  const latest = sortContractsLatestFirst(contracts)[0]
  if (latest === undefined) {
    return { lastPrice: undefined, executedVolume: '0', executedCount: 0 }
  }

  let totalVolume = 0
  for (const contract of contracts) {
    const volume = contract.volume?.value
    totalVolume +=
      volume !== undefined && volume !== '' ? parseFloat(volume) : 0
  }

  const lastPrice = latest.price?.value
  return {
    lastPrice:
      lastPrice !== undefined && lastPrice !== '' ? lastPrice : undefined,
    executedVolume: String(totalVolume),
    executedCount: contracts.length,
  }
}

/** 板情報パネルフッターの 3 指標 (AC-BIN-026: 最良売り価格 / 最良買い価格 / スプレッド)。 */
export type MarketBoardBestQuotes = {
  /** 最良売り価格 (= 売り深さのある最安値、 円/kWh)。 売り板が空なら undefined。 */
  bestAsk: string | undefined
  /** 最良買い価格 (= 買い深さのある最高値、 円/kWh)。 買い板が空なら undefined。 */
  bestBid: string | undefined
  /** 最良売り価格 − 最良買い価格 (円)。 どちらか片側が無ければ undefined。 */
  spread: string | undefined
  /** 最良売り価格 < 最良買い価格 (= スプレッドが負、 板が交差している)。 #9190 */
  isCrossed: boolean
}

/**
 * pivot 済み板データ (`pivotMarketBoard` の出力) から最良気配とスプレッドを算出する。
 *
 * 順序に依存しない (= 呼び出し側の並び順を前提にしない): 各行の売り / 買いを
 * sell / buy の有無で判定し、 売りは最安値・買いは最高値を取る
 * (order-book 慣行: best ask = 売り深さのある最安値 / best bid = 買い深さのある最高値)。
 */
export function computeMarketBoardBestQuotes(
  rows: MarketBoardRow[],
): MarketBoardBestQuotes {
  let bestAskNum: number | undefined
  let bestBidNum: number | undefined

  for (const row of rows) {
    if (row.sell !== undefined) {
      const price = parseFloat(row.sell.price)
      bestAskNum =
        bestAskNum === undefined ? price : Math.min(bestAskNum, price)
    }
    if (row.buy !== undefined) {
      const price = parseFloat(row.buy.price)
      bestBidNum =
        bestBidNum === undefined ? price : Math.max(bestBidNum, price)
    }
  }

  return {
    bestAsk: bestAskNum !== undefined ? String(bestAskNum) : undefined,
    bestBid: bestBidNum !== undefined ? String(bestBidNum) : undefined,
    spread:
      bestAskNum !== undefined && bestBidNum !== undefined
        ? String(bestAskNum - bestBidNum)
        : undefined,
    isCrossed:
      bestAskNum !== undefined &&
      bestBidNum !== undefined &&
      bestAskNum < bestBidNum,
  }
}
